// src/components/ui/ChoiceMatrix.tsx
// The `choice-matrix` field type's grid — Pre-Consult redesign Phase 2. One
// question asked identically of several subjects (rows) on one shared scale
// (columns), read together — the Diet Assessment card is the motivating
// case. See docs/HMS_V4_ASSESSMENT_FIELD_TYPES_SPEC.md §2 for the full
// contract this renders.
//
// Semantics: one `role="radiogroup"` per ROW (not per column) — a row is
// "exactly one of these options", and the whole point of a matrix is reading
// several rows' verdicts together, not choosing one column across all rows.
import { StatusFace, type IllustrationTone } from './illustrations';
import { COLOR_TONE, TONE_TEXT, TONE_BORDER_BG, renderChoiceIllustration } from './choiceIllustration';
import { cn } from '@/lib/cn';
import type { MatrixColumn, MatrixRow, MatrixCell } from '@/lib/assessmentTemplate';

/** A cell's own colour wins over its column's; a cell/column authored with no
 * colour at all falls back to 'warn' — a deliberately neutral middle rather
 * than guessing pass/fail from nothing. */
function cellTone(cell: MatrixCell, col: MatrixColumn): IllustrationTone {
  return COLOR_TONE[cell.color ?? col.color ?? 'yellow'];
}

function DesktopCell({ row, col, rowAnswer, onSelect, disabled }: {
  row: MatrixRow; col: MatrixColumn; rowAnswer: string | undefined;
  onSelect: (option: string) => void; disabled?: boolean;
}) {
  const cell = row.cells[col.value];
  if (!cell) {
    // Cell not authored for this row/column pair — empty and unselectable,
    // never shifted, so the grid stays aligned (field-types spec §4).
    return (
      <td className="px-1.5 py-1.5 text-center">
        <div className="mx-auto h-9 w-9 rounded-lg border border-dashed border-control-line opacity-50" />
      </td>
    );
  }
  const tone = cellTone(cell, col);
  const selected = rowAnswer === cell.option;
  return (
    <td className="px-1.5 py-1.5 text-center">
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        aria-label={`${row.label}, ${cell.option}`}
        disabled={disabled}
        onClick={() => onSelect(cell.option)}
        className={cn(
          'mx-auto flex w-full max-w-[92px] flex-col items-center gap-1 rounded-lg border px-1.5 py-1.5',
          selected ? TONE_BORDER_BG[tone] : 'border-transparent hover:border-line',
          disabled && 'pointer-events-none disabled:opacity-100'
        )}
      >
        {renderChoiceIllustration(cell.illustration, tone, 'md')}
        <span className={cn('text-[10.5px] leading-tight', selected ? cn('font-semibold', TONE_TEXT[tone]) : 'text-ink-muted')}>
          {cell.option}
        </span>
      </button>
    </td>
  );
}

function YourStatusCell({ row, columns, rowAnswer }: { row: MatrixRow; columns: MatrixColumn[]; rowAnswer: string | undefined }) {
  const col = columns.find(c => row.cells[c.value]?.option === rowAnswer);
  const cell = col ? row.cells[col.value] : undefined;
  if (!cell || !col) {
    return <td className="px-1.5 py-1.5 text-center text-[12px] text-ink-muted">—</td>;
  }
  const tone = cellTone(cell, col);
  return (
    <td className="px-1.5 py-1.5 text-center">
      <div className={cn('mx-auto flex w-fit flex-col items-center gap-1 rounded-lg border px-2 py-1.5', TONE_BORDER_BG[tone])}>
        <StatusFace tone={tone} size="sm" />
        <span className={cn('text-[10.5px] font-semibold leading-tight', TONE_TEXT[tone])}>{cell.option}</span>
      </div>
    </td>
  );
}

function MobileRowCard({ row, columns, rowAnswer, onSelect, disabled }: {
  row: MatrixRow; columns: MatrixColumn[]; rowAnswer: string | undefined;
  onSelect: (option: string) => void; disabled?: boolean;
}) {
  return (
    <div className="rounded-[var(--radius)] border border-line p-2.5" role="radiogroup" aria-label={row.label}>
      <div className="text-[12.5px] font-semibold text-ink">{row.label}</div>
      {row.hint && <div className="text-[10px] text-ink-muted">{row.hint}</div>}
      {row.cutoff && <div className="mt-0.5 text-[11px] font-medium text-ink-muted">Cut-off: {row.cutoff}</div>}
      <div className="mt-1.5 flex flex-col gap-1.5">
        {columns.map(col => {
          const cell = row.cells[col.value];
          if (!cell) return null;
          const tone = cellTone(cell, col);
          const selected = rowAnswer === cell.option;
          return (
            <button
              key={col.value}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={`${row.label}, ${cell.option}`}
              disabled={disabled}
              onClick={() => onSelect(cell.option)}
              className={cn(
                'flex items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left',
                selected ? TONE_BORDER_BG[tone] : 'border-line',
                disabled && 'pointer-events-none disabled:opacity-100'
              )}
            >
              {renderChoiceIllustration(cell.illustration, tone, 'sm')}
              <span className={cn('flex-1 text-[12px]', selected && cn('font-semibold', TONE_TEXT[tone]))}>{cell.option}</span>
              {selected && <span className={cn('text-[11px] font-bold', TONE_TEXT[tone])}>✓</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export interface ChoiceMatrixProps {
  columns: MatrixColumn[];
  rows: MatrixRow[];
  value: Record<string, string> | undefined;
  onChange: (value: Record<string, string>) => void;
  /** See SegmentedControl's own doc comment — a selected cell must stay fully
   * legible inside a disabled `<fieldset>` (StageEditModal's read-only view),
   * not fade along with everything else. */
  disabled?: boolean;
  footnote?: string;
}

/** The diet-matrix-shaped field type. Below `lg` (StageEditModal gives this
 * ~600px, well under that), the table collapses to one stacked card per row
 * — no horizontal scroll at any width, per the field-types spec's rendering
 * notes. Both layouts share the same row/column data and selection handler;
 * only one is ever visible at a given width. */
export function ChoiceMatrix({ columns, rows, value, onChange, disabled, footnote }: ChoiceMatrixProps) {
  const answers = value || {};
  const hasCutoff = rows.some(r => r.cutoff);
  const select = (rowKey: string, option: string) => onChange({ ...answers, [rowKey]: option });

  return (
    <div>
      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="w-[28%]" />
              {hasCutoff && <th className="pb-2 text-[9.5px] font-extrabold uppercase tracking-wide text-ink-muted">Cut-off</th>}
              {columns.map(col => (
                <th
                  key={col.value}
                  className={cn(
                    'px-1.5 pb-2 text-[9.5px] font-extrabold uppercase tracking-wide',
                    col.color ? TONE_TEXT[COLOR_TONE[col.color]] : 'text-ink-muted'
                  )}
                >
                  {col.label}
                </th>
              ))}
              <th className="px-1.5 pb-2 text-[9.5px] font-extrabold uppercase tracking-wide text-info-ink">Your status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const rowAnswer = answers[row.key];
              return (
                <tr key={row.key} role="radiogroup" aria-label={row.label} className="border-t border-line first:border-t-0">
                  <td className="py-1.5 pr-2 align-middle">
                    <div className="text-[12.5px] font-semibold text-ink">{row.label}</div>
                    {row.hint && <div className="text-[10px] leading-snug text-ink-muted">{row.hint}</div>}
                  </td>
                  {hasCutoff && <td className="px-1.5 py-1.5 text-center text-[11.5px] font-semibold text-ink-muted">{row.cutoff}</td>}
                  {columns.map(col => (
                    <DesktopCell key={col.value} row={row} col={col} rowAnswer={rowAnswer} onSelect={opt => select(row.key, opt)} disabled={disabled} />
                  ))}
                  <YourStatusCell row={row} columns={columns} rowAnswer={rowAnswer} />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-3 lg:hidden">
        {rows.map(row => (
          <MobileRowCard key={row.key} row={row} columns={columns} rowAnswer={answers[row.key]} onSelect={opt => select(row.key, opt)} disabled={disabled} />
        ))}
      </div>

      {footnote && (
        <div className="mt-2.5 rounded-lg bg-info-soft px-2.5 py-1.5 text-[11px] text-info-ink">ⓘ {footnote}</div>
      )}
    </div>
  );
}
